// src/services/errorReporting.js
// Centralized error reporting utility with light sanitization.

import * as Sentry from '@sentry/react';

export const ErrorSeverity = {
  ERROR: 'error',
  WARNING: 'warning',
};

// Very basic sanitizer: redact any token that starts with `password` (case-insensitive)
// e.g., "password123" => "[REDACTED]"
function sanitizeMessage(message) {
  if (!message) return message;
  return String(message).replace(/password\w*/gi, '[REDACTED]');
}

function toError(input) {
  if (input instanceof Error) return input;
  return new Error(String(input));
}

export function reportError(errorOrMessage, severity = ErrorSeverity.ERROR, extra = {}) {
  if (severity === ErrorSeverity.WARNING) {
    // Do not send to Sentry for warnings; just log.
    // Keep the log minimal to avoid leaking sensitive info in CI logs.
    // eslint-disable-next-line no-console
    console.warn('Warning:', typeof errorOrMessage === 'string' ? errorOrMessage : errorOrMessage?.message);
    return;
  }

  // Sanitize and send to Sentry for errors
  const originalError = toError(errorOrMessage);
  const sanitizedMessage = sanitizeMessage(originalError.message);
  const toSend = sanitizedMessage === originalError.message
    ? originalError // no sensitive token -> send original instance so strict equality in tests holds
    : (() => {
        const copy = new Error(sanitizedMessage);
        copy.name = originalError.name;
        if (originalError.stack) copy.stack = originalError.stack;
        return copy;
      })();

  // Level mapping follows Sentry levels; "error" is sufficient for our current tests/usage
  Sentry.captureException(toSend, {
    level: 'error',
    extra,
  });
}
